import React, { useState } from 'react';
import { Stack } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';
import { APIRequestPagination, APIRequestSort } from 'global';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { MUIBaseEntity, MUITable } from 'components/shared/MUITable';
import { InventoryItem } from '@trnsact/trnsact-shared-types/dist/generated';

import { createTableColumns } from './tableConfig';
import { InventoryTableFilters } from './InventoryTableFilters';

const Q_INVENTORY_LIST = gql`
  query GetInventoryList($input: InventoryListInput!) {
    inventoryList(input: $input) {
      data {
        condition
        description
        make
        mileage
        model
        retailPrice
        source
        stockNumber
        vin
        year
        inventoryId
        equipmentId
        status
      }
      page
      perPage
      total
      totalPages
    }
  }
`;

interface Props {
  accountId: string;
}

interface InventoryListResponse {
  page: number;
  total: number;
  perPage: number;
  totalPages: number;
  data: InventoryItem[];
}

export interface InventoryItemEntity extends MUIBaseEntity, InventoryItem {}

export const InventoryTable = ({ accountId }: Props) => {
  const history = useHistory();

  const form = useForm({
    defaultValues: {
      filters: {},
    },
  });

  const filters = useWatch({ control: form.control, name: 'filters' });

  const [pagination, setPagination] = useState<APIRequestPagination>({ page: 1, perPage: 15 });
  const [sorting, setSorting] = useState<APIRequestSort>({ key: 'make', value: 'asc' });

  const { data, loading, previousData } = useQuery<{ inventoryList: InventoryListResponse }>(Q_INVENTORY_LIST, {
    variables: {
      accountId,
      input: { accountId, pagination, sort: sorting, filters },
    },
  });

  const columns = createTableColumns({ useHistory: history });

  const tableData = ((data ?? previousData)?.inventoryList?.data ?? []).map(inventoryItem => ({
    id: inventoryItem.inventoryId!,
    condition: inventoryItem.condition,
    description: inventoryItem.description ?? '',
    make: inventoryItem.make,
    mileage: inventoryItem.mileage,
    model: inventoryItem.model,
    retailPrice: inventoryItem.retailPrice,
    source: inventoryItem.source,
    stockNumber: inventoryItem.stockNumber,
    vin: inventoryItem.vin,
    year: inventoryItem.year,
    equipmentId: inventoryItem.equipmentId,
    inventoryId: inventoryItem.inventoryId,
    status: inventoryItem.status,
  }));

  return (
    <FormProvider {...form}>
      <Stack gap="0.5rem" height={`calc(100% - ${tableData.length ? '5rem' : '0rem'})`}>
        <InventoryTableFilters />

        <MUITable<InventoryItemEntity>
          stickyHeader
          columns={columns}
          isLoading={loading}
          tableData={tableData}
          sorting={{ ...sorting, onChange: setSorting }}
          onRowClick={(_event, row) => {
            if (row.equipmentId) {
              history.push(`/equipment/${row.equipmentId}/details`);
            }
          }}
          pagination={{
            ...pagination,
            total: data?.inventoryList?.total ?? 0,
            onChange: nextValue => setPagination(prev => ({ ...prev, ...nextValue })),
          }}
        />
      </Stack>
    </FormProvider>
  );
};
